import type { PartialMessage } from '@bufbuild/protobuf';

import {
  AnalyticsModel as AnalyticsModelMessage,
  AnalyticsModelSummary as AnalyticsModelSummaryMessage,
  AnalyticsSectionSpec as AnalyticsSectionSpecMessage,
  AnalyticsTagsConfig as AnalyticsTagsConfigMessage,
  CatalogCloneResult as CatalogCloneResultMessage,
  CatalogListing as CatalogListingMessage,
  CatalogStatus,
  ChecklistGenerationStatus,
  CommunityDocumentTemplate as CommunityDocumentTemplateMessage,
  DocumentSectionSpec as DocumentSectionSpecMessage,
  DocumentTemplate as DocumentTemplateMessage,
  DocumentTemplateSummary as DocumentTemplateSummaryMessage,
  EncounterSchema as EncounterSchemaMessage,
  EncounterSchemaSummary as EncounterSchemaSummaryMessage,
  RegistryAsset as RegistryAssetMessage,
  RegistryAssetFamily as RegistryAssetFamilyEnum,
  RegistryOriginKind as RegistryOriginKindEnum,
  RegistrySourceKind as RegistrySourceKindEnum,
  SchemaDocumentInclusion,
  SchemaDocumentRef as SchemaDocumentRefMessage,
  SectionOption as SectionOptionMessage,
  TemplateChecklist as TemplateChecklistMessage,
} from '@/shared/api/protocol-ts/model/dto_scribe_catalog_pb';

import type {
  AnalyticsModel,
  AnalyticsModelSummary,
  AnalyticsSection,
  AnalyticsTagsConfig,
} from '@/entities/analyticsModel/config';
import type {
  CatalogListing,
  ChecklistGenerationStatus as ChecklistStatus,
  DocumentSectionSpec,
  DocumentTemplate,
  DocumentTemplateSummary,
  DocumentType,
  EncounterSchema,
  SchemaDocumentRef,
  SectionOptionSchema,
  TemplateChecklistResponse,
} from '@/entities/templates/config';

import type {
  CatalogCloneResult,
  CatalogStatusName,
  CommunityTemplateDetailResponse,
  RegistryAsset,
  RegistryAssetFamily,
  RegistryOriginKind,
  RegistrySourceKind,
} from './scribeCatalogTypes';

// scribe stores document types in snake case, the client keeps the screaming
// case its components and form state are written against.
const DOCUMENT_TYPE_TO_DOMAIN: Record<string, DocumentType> = {
  evaluation: 'EVALUATION',
  summary: 'SUMMARY',
  protocol: 'PROTOCOL',
  literature_review: 'LITERATURE_REVIEW',
  follow_up_letter: 'FOLLOW_UP_LETTER',
  letter: 'FOLLOW_UP_LETTER',
};

const DOCUMENT_TYPE_TO_WIRE: Record<string, string> = {
  EVALUATION: 'evaluation',
  SUMMARY: 'summary',
  PROTOCOL: 'protocol',
  LITERATURE_REVIEW: 'literature_review',
  FOLLOW_UP_LETTER: 'follow_up_letter',
  evaluation: 'evaluation',
  summary: 'summary',
  protocol: 'protocol',
  literature_review: 'literature_review',
  follow_up_letter: 'follow_up_letter',
  letter: 'follow_up_letter',
};

const PROCESSING_TYPE_TO_DOMAIN: Record<
  string,
  DocumentSectionSpec['processing_type']
> = {
  standard: 'STANDARD',
  extended: 'EXTENDED',
};

const PROCESSING_TYPE_TO_WIRE: Record<string, string> = {
  STANDARD: 'standard',
  EXTENDED: 'extended',
  standard: 'standard',
  extended: 'extended',
};

const REGISTRY_FAMILY_TO_DOMAIN: Record<
  RegistryAssetFamilyEnum,
  RegistryAssetFamily
> = {
  [RegistryAssetFamilyEnum.RegistryAssetFamilyInvalidValue]:
    'document_template',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyDocumentTemplate]:
    'document_template',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyEncounterSchema]:
    'encounter_schema',
  [RegistryAssetFamilyEnum.RegistryAssetFamilyAnalyticsModel]:
    'analytics_model',
};

const REGISTRY_FAMILY_TO_WIRE: Record<
  RegistryAssetFamily,
  RegistryAssetFamilyEnum
> = {
  document_template:
    RegistryAssetFamilyEnum.RegistryAssetFamilyDocumentTemplate,
  encounter_schema: RegistryAssetFamilyEnum.RegistryAssetFamilyEncounterSchema,
  analytics_model: RegistryAssetFamilyEnum.RegistryAssetFamilyAnalyticsModel,
};

const REGISTRY_SOURCE_KIND_TO_DOMAIN: Record<
  RegistrySourceKindEnum,
  RegistrySourceKind
> = {
  [RegistrySourceKindEnum.RegistrySourceKindInvalidValue]: 'system',
  [RegistrySourceKindEnum.RegistrySourceKindSystem]: 'system',
  [RegistrySourceKindEnum.RegistrySourceKindCommunity]: 'community',
};

const REGISTRY_SOURCE_KIND_TO_WIRE: Record<
  RegistrySourceKind,
  RegistrySourceKindEnum
> = {
  system: RegistrySourceKindEnum.RegistrySourceKindSystem,
  community: RegistrySourceKindEnum.RegistrySourceKindCommunity,
};

const REGISTRY_ORIGIN_KIND_TO_DOMAIN: Record<
  RegistryOriginKindEnum,
  RegistryOriginKind
> = {
  [RegistryOriginKindEnum.RegistryOriginKindInvalidValue]: 'official',
  [RegistryOriginKindEnum.RegistryOriginKindOfficial]: 'official',
  [RegistryOriginKindEnum.RegistryOriginKindCommunity]: 'community',
};

const CHECKLIST_STATUS_TO_DOMAIN: Record<
  ChecklistGenerationStatus,
  ChecklistStatus
> = {
  [ChecklistGenerationStatus.ChecklistGenerationStatusInvalidValue]: 'pending',
  [ChecklistGenerationStatus.ChecklistGenerationStatusPending]: 'pending',
  [ChecklistGenerationStatus.ChecklistGenerationStatusProcessing]: 'processing',
  [ChecklistGenerationStatus.ChecklistGenerationStatusCompleted]: 'completed',
  [ChecklistGenerationStatus.ChecklistGenerationStatusFailed]: 'failed',
};

export const documentTypeToWire = (type?: string): string | undefined =>
  type ? DOCUMENT_TYPE_TO_WIRE[type] ?? type : undefined;

export const documentTypeToDomain = (type: string): DocumentType =>
  DOCUMENT_TYPE_TO_DOMAIN[type] ?? (type as DocumentType);

export const processingTypeToDomain = (
  processingType?: string,
): DocumentSectionSpec['processing_type'] =>
  processingType ? PROCESSING_TYPE_TO_DOMAIN[processingType] : undefined;

// The three statuses scribe stores; the wider client-side VersionStatus adds
// `archived`, which no server response can carry.
export const catalogStatusToDomain = (
  status: CatalogStatus,
): CatalogStatusName => {
  switch (status) {
    case CatalogStatus.CatalogStatusDraft:
      return 'draft';
    case CatalogStatus.CatalogStatusDeleted:
      return 'deleted';
    default:
      return 'active';
  }
};

export const catalogStatusToWire = (
  status?: string,
): CatalogStatus | undefined => {
  switch (status) {
    case 'active':
      return CatalogStatus.CatalogStatusActive;
    case 'draft':
      return CatalogStatus.CatalogStatusDraft;
    case 'deleted':
      return CatalogStatus.CatalogStatusDeleted;
    default:
      return undefined;
  }
};

export const catalogStatusesToWire = (
  statuses?: CatalogStatusName[],
): CatalogStatus[] | undefined =>
  statuses
    ?.map(catalogStatusToWire)
    .filter((status): status is CatalogStatus => status !== undefined);

export const registryFamilyToWire = (
  family?: RegistryAssetFamily,
): RegistryAssetFamilyEnum | undefined =>
  family ? REGISTRY_FAMILY_TO_WIRE[family] : undefined;

export const registrySourceKindToWire = (
  sourceKind?: RegistrySourceKind,
): RegistrySourceKindEnum | undefined =>
  sourceKind ? REGISTRY_SOURCE_KIND_TO_WIRE[sourceKind] : undefined;

const inclusionToDomain = (
  inclusion: SchemaDocumentInclusion,
): SchemaDocumentRef['inclusion'] =>
  inclusion === SchemaDocumentInclusion.SchemaDocumentInclusionOnDemand
    ? 'on_demand'
    : 'always';

const inclusionToWire = (
  inclusion?: SchemaDocumentRef['inclusion'],
): SchemaDocumentInclusion =>
  inclusion === 'on_demand'
    ? SchemaDocumentInclusion.SchemaDocumentInclusionOnDemand
    : SchemaDocumentInclusion.SchemaDocumentInclusionAlways;

const emptyToUndefined = <T extends Record<string, unknown>>(
  value: T,
): T | undefined => (Object.keys(value).length > 0 ? value : undefined);

const sectionOptionToDomain = (
  option: SectionOptionMessage,
): SectionOptionSchema => ({
  text: option.Text,
  slots: emptyToUndefined(
    Object.fromEntries(
      Object.entries(option.Slots).map(([slot, values]) => [
        slot,
        values.Values,
      ]),
    ),
  ),
  instructions: option.Instructions,
  slot_instructions: emptyToUndefined({ ...option.SlotInstructions }),
});

const sectionOptionToWire = (
  option: SectionOptionSchema,
): PartialMessage<SectionOptionMessage> => ({
  Text: option.text,
  Slots: Object.fromEntries(
    Object.entries(option.slots ?? {}).map(([slot, values]) => [
      slot,
      { Values: values },
    ]),
  ),
  Instructions: option.instructions,
  SlotInstructions: option.slot_instructions ?? {},
});

export const documentSectionToDomain = (
  section: DocumentSectionSpecMessage,
): DocumentSectionSpec => ({
  id: section.ID || undefined,
  code: section.Code,
  title: section.Title,
  category: section.Category,
  editor_type: section.EditorType as DocumentSectionSpec['editor_type'],
  type: section.Type as DocumentSectionSpec['type'],
  processing_type: processingTypeToDomain(section.ProcessingType),
  instructions: section.Instructions,
  options: section.Options.map(sectionOptionToDomain),
  template_type: section.TemplateType as DocumentSectionSpec['template_type'],
  required: section.Required,
});

export const documentSectionToWire = (
  section: DocumentSectionSpec,
): PartialMessage<DocumentSectionSpecMessage> => ({
  ID: section.id,
  Code: section.code,
  Title: section.title,
  Category: section.category,
  EditorType: section.editor_type,
  Type: section.type,
  ProcessingType: section.processing_type
    ? PROCESSING_TYPE_TO_WIRE[section.processing_type] ??
      section.processing_type
    : undefined,
  Instructions: section.instructions,
  Options: (section.options ?? []).map(sectionOptionToWire),
  TemplateType: section.template_type,
  Required: section.required,
});

const checklistToDomain = (
  checklist: TemplateChecklistMessage,
): TemplateChecklistResponse => ({
  status: CHECKLIST_STATUS_TO_DOMAIN[checklist.Status],
  error: checklist.Error,
  content: checklist.Content && {
    items: checklist.Content.Items,
    generated_at: checklist.Content.GeneratedAt?.toDate().toISOString() ?? '',
    model: checklist.Content.Model,
  },
});

const catalogListingToDomain = (
  listing: CatalogListingMessage,
): CatalogListing => ({
  specialty: listing.Specialty,
  category: listing.Category,
  tags: listing.Tags,
  import_count: listing.ImportCount,
  description: listing.Description,
  location: listing.Location,
  published_at: listing.PublishedAt?.toDate().toISOString(),
});

export const documentTemplateSummaryToDomain = (
  template: DocumentTemplateSummaryMessage,
): DocumentTemplateSummary => ({
  id: template.ID,
  series_id: template.SeriesID,
  code: template.Code,
  language: template.Language,
  version: template.Version,
  status: catalogStatusToDomain(template.Status),
  organization_id: template.OrganizationID,
  source_kind: template.SourceKind as DocumentTemplateSummary['source_kind'],
  derived_from_kind:
    template.DerivedFromKind as DocumentTemplateSummary['derived_from_kind'],
  derived_from_id: template.DerivedFromID,
  type: documentTypeToDomain(template.Type),
  name: template.Name,
  tab_title: template.TabTitle,
  description: template.Description,
  sections_count: template.SectionsCount,
  catalog_published: template.CatalogPublished,
  created_at: template.CreatedAt?.toDate().toISOString() ?? '',
  updated_at: template.UpdatedAt?.toDate().toISOString(),
});

export const documentTemplateToDomain = (
  template: DocumentTemplateMessage = new DocumentTemplateMessage(),
): DocumentTemplate => ({
  id: template.ID,
  series_id: template.SeriesID,
  code: template.Code,
  language: template.Language,
  version: template.Version,
  status: catalogStatusToDomain(template.Status),
  is_system: template.IsSystem,
  organization_id: template.OrganizationID,
  source_kind: template.SourceKind as DocumentTemplate['source_kind'],
  derived_from_kind:
    template.DerivedFromKind as DocumentTemplate['derived_from_kind'],
  derived_from_id: template.DerivedFromID,
  type: documentTypeToDomain(template.Type),
  name: template.Name,
  tab_title: template.TabTitle,
  description: template.Description,
  sections: template.Sections.map(documentSectionToDomain),
  catalog_published: template.CatalogPublished,
  catalog_metadata:
    template.CatalogListing && catalogListingToDomain(template.CatalogListing),
  checklist: template.Checklist && checklistToDomain(template.Checklist),
  source_document: template.SourceDocument,
  created_at: template.CreatedAt?.toDate().toISOString() ?? '',
  updated_at: template.UpdatedAt?.toDate().toISOString(),
});

const schemaDocumentRefToDomain = (
  document: SchemaDocumentRefMessage,
): SchemaDocumentRef => ({
  template_series_id: document.TemplateSeriesID,
  code: document.Code ?? '',
  inclusion: inclusionToDomain(document.Inclusion),
  name: document.Name,
  tab_title: document.TabTitle,
  type: document.Type ? documentTypeToDomain(document.Type) : undefined,
});

export const schemaDocumentRefToWire = (
  document: Pick<SchemaDocumentRef, 'template_series_id' | 'inclusion'>,
): PartialMessage<SchemaDocumentRefMessage> => ({
  TemplateSeriesID: document.template_series_id ?? '',
  Inclusion: inclusionToWire(document.inclusion),
});

export const encounterSchemaSummaryToDomain = (
  schema: EncounterSchemaSummaryMessage,
): EncounterSchema => ({
  id: schema.ID,
  code: schema.Code,
  language: schema.Language,
  status: catalogStatusToDomain(schema.Status),
  organization_id: schema.OrganizationID,
  source_kind: schema.SourceKind as EncounterSchema['source_kind'],
  derived_from_kind:
    schema.DerivedFromKind as EncounterSchema['derived_from_kind'],
  derived_from_id: schema.DerivedFromID,
  name: schema.Name,
  description: schema.Description,
  analytics_model_series_id: schema.AnalyticsModelSeriesID,
  documents: schema.Documents.map(schemaDocumentRefToDomain),
  document_count: schema.DocumentCount,
  created_at: schema.CreatedAt?.toDate().toISOString(),
});

export const encounterSchemaToDomain = (
  schema: EncounterSchemaMessage = new EncounterSchemaMessage(),
): EncounterSchema => ({
  id: schema.ID,
  code: schema.Code,
  language: schema.Language,
  status: catalogStatusToDomain(schema.Status),
  is_system: schema.IsSystem,
  organization_id: schema.OrganizationID,
  source_kind: schema.SourceKind as EncounterSchema['source_kind'],
  derived_from_kind:
    schema.DerivedFromKind as EncounterSchema['derived_from_kind'],
  derived_from_id: schema.DerivedFromID,
  name: schema.Name,
  description: schema.Description,
  analytics_model_series_id: schema.AnalyticsModelSeriesID,
  documents: schema.Documents.map(schemaDocumentRefToDomain),
  always_document_codes: schema.AlwaysDocumentCodes,
  on_demand_document_codes: schema.OnDemandDocumentCodes,
  always_document_series_ids: schema.AlwaysDocumentSeriesIDs,
  on_demand_document_series_ids: schema.OnDemandDocumentSeriesIDs,
  document_count: schema.DocumentCount,
  created_at: schema.CreatedAt?.toDate().toISOString(),
  created_by: schema.CreatedBy,
});

const analyticsTagsToDomain = (
  tags?: AnalyticsTagsConfigMessage,
): AnalyticsTagsConfig => ({
  instructions: tags?.Instructions,
  groups: (tags?.Groups ?? []).map((group) => ({
    code: group.Code,
    name: group.Name,
    type: group.Type as AnalyticsTagsConfig['groups'][number]['type'],
    instructions: group.Instructions,
    selection_mode:
      group.SelectionMode as AnalyticsTagsConfig['groups'][number]['selection_mode'],
    required: group.Required,
    tags: group.Tags.map((tag) => ({
      code: tag.Code,
      name: tag.Name,
      instructions: tag.Instructions,
      explain: tag.Explain,
    })),
  })),
});

export const analyticsTagsToWire = (
  tags?: AnalyticsTagsConfig,
): PartialMessage<AnalyticsTagsConfigMessage> | undefined =>
  tags && {
    Instructions: tags.instructions ?? undefined,
    Groups: tags.groups.map((group) => ({
      Code: group.code,
      Name: group.name,
      Type: group.type,
      Instructions: group.instructions,
      SelectionMode: group.selection_mode,
      Required: group.required,
      Tags: group.tags.map((tag) => ({
        Code: tag.code,
        Name: tag.name,
        Instructions: tag.instructions,
        Explain: tag.explain,
      })),
    })),
  };

const analyticsSectionToDomain = (
  section: AnalyticsSectionSpecMessage,
): AnalyticsSection => ({
  code: section.Code,
  name: section.Name,
  kind: section.Kind as AnalyticsSection['kind'],
  instructions: section.Instructions,
});

export const analyticsSectionToWire = (
  section: AnalyticsSection,
): PartialMessage<AnalyticsSectionSpecMessage> => ({
  Code: section.code,
  Name: section.name,
  Kind: section.kind,
  Instructions: section.instructions,
});

export const analyticsModelSummaryToDomain = (
  model: AnalyticsModelSummaryMessage,
): AnalyticsModelSummary => ({
  id: model.ID,
  series_id: model.SeriesID,
  code: model.Code,
  language: model.Language,
  version: model.Version,
  status: catalogStatusToDomain(model.Status),
  organization_id: model.OrganizationID,
  source_kind: model.SourceKind as AnalyticsModel['source_kind'],
  derived_from_kind:
    model.DerivedFromKind as AnalyticsModel['derived_from_kind'],
  derived_from_id: model.DerivedFromID,
  name: model.Name,
  created_at: model.CreatedAt?.toDate().toISOString() ?? '',
});

export const analyticsModelToDomain = (
  model: AnalyticsModelMessage = new AnalyticsModelMessage(),
): AnalyticsModel => ({
  id: model.ID,
  series_id: model.SeriesID,
  code: model.Code,
  language: model.Language,
  version: model.Version,
  status: catalogStatusToDomain(model.Status),
  is_system: model.IsSystem,
  organization_id: model.OrganizationID,
  source_kind: model.SourceKind as AnalyticsModel['source_kind'],
  derived_from_kind:
    model.DerivedFromKind as AnalyticsModel['derived_from_kind'],
  derived_from_id: model.DerivedFromID,
  name: model.Name,
  tags: analyticsTagsToDomain(model.Tags),
  sections: model.Sections.map(analyticsSectionToDomain),
  created_at: model.CreatedAt?.toDate().toISOString() ?? '',
});

export const communityTemplateToDomain = (
  template: CommunityDocumentTemplateMessage = new CommunityDocumentTemplateMessage(),
): CommunityTemplateDetailResponse => ({
  id: template.ID,
  code: template.Code,
  name: template.Name,
  description: template.Description,
  type: template.Type,
  language: template.Language,
  tab_title: template.TabTitle,
  specialty: template.Specialty,
  category: template.Category,
  location: template.Location,
  tags: template.Tags,
  author_display_name: template.AuthorDisplayName,
  author_specialty: template.AuthorSpecialty,
  source_template_id: template.SourceTemplateID,
  source_series_id: template.SourceSeriesID,
  source_organization_id: template.SourceOrganizationID,
  sections: template.Sections.map(documentSectionToDomain),
  import_count: template.ImportCount,
  sections_count: template.SectionsCount,
  published_at: template.PublishedAt?.toDate().toISOString(),
});

export const registryAssetToDomain = (
  asset: RegistryAssetMessage,
): RegistryAsset => ({
  id: asset.ID,
  family: REGISTRY_FAMILY_TO_DOMAIN[asset.Family],
  sourceKind: REGISTRY_SOURCE_KIND_TO_DOMAIN[asset.SourceKind],
  originKind: REGISTRY_ORIGIN_KIND_TO_DOMAIN[asset.OriginKind],
  language: asset.Language,
  name: asset.Name,
  description: asset.Description ?? null,
  templateType: (asset.TemplateType ?? null) as RegistryAsset['templateType'],
  sectionsCount: asset.SectionsCount ?? null,
  documentCount: asset.DocumentCount ?? null,
  analyticsGroupsCount: asset.AnalyticsGroupsCount ?? null,
  analyticsSectionsCount: asset.AnalyticsSectionsCount ?? null,
  authorDisplayName: asset.AuthorDisplayName ?? null,
  authorSpecialty: asset.AuthorSpecialty ?? null,
  specialty: asset.Specialty ?? null,
  category: asset.Category ?? null,
  importCount: asset.ImportCount ?? null,
  hasLocalClone: asset.HasLocalClone,
  localCloneId: asset.LocalCloneID ?? null,
});

export const cloneResultToDomain = (
  result?: CatalogCloneResultMessage,
): CatalogCloneResult => ({
  local_asset_id: result?.LocalAssetID ?? '',
  local_series_id: result?.LocalSeriesID,
  reused_existing: result?.ReusedExisting ?? false,
});
